import { nodes, ExtractStrongNodeSpecAttributes } from 'prose-core'
import { Node as ProseNode } from 'prosemirror-model'
import { Transaction } from 'prosemirror-state'
import { merge, times } from 'lodash'
import { stripTrailingZeroes } from 'utils/arrayUtils'

export const docToMarkdown = (tr: Transaction) => {
  const { selection } = tr

  // const startPos = getCuttingBoundaries(tr.doc.nodeSize - 2, selection.from, 4000, true)
  const [startPos, endPos] = getCuttingBoundaries(tr.doc.nodeSize - 2, selection.from, 4000)

  const oldDoc = tr.doc
  tr.insertText('>|<')

  const contextDoc = tr.doc.cut(startPos, endPos)
  const markdownDoc = convertToMarkdown(contextDoc)

  tr.steps = []
  tr.doc = oldDoc

  return markdownDoc
}

const convertToMarkdown = (doc: ProseNode): string => {
  let markdown = ''
  let currentListItemNumber = times(10, () => 0)
  doc.descendants((node, _, parent) => {
    let shouldDescend = true
    switch (node.type.name) {
      case 'paragraph':
        if (!['', '\n'].includes(node.textContent)) {
          markdown += node.textContent + '\n'
        }
        shouldDescend = false
        break
      case 'heading':
        markdown += `${'#'.repeat((node.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.heading>).level)} ${
          node.textContent
        }\n`
        shouldDescend = false
        break
      case 'ordered_list': {
        const listStartOffset = (node.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.ordered_list>)
          .listStartOffset
        const listStartOffsetAsArray = Array.isArray(listStartOffset) ? listStartOffset : [listStartOffset]
        currentListItemNumber = merge(currentListItemNumber, listStartOffsetAsArray, [0])
        break
      }
      case 'column_group':
      case 'column':
      case 'unordered_list':
        break
      case 'list_item': {
        const listItemAttrs = node.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.list_item>
        switch (parent.type.name) {
          case 'ordered_list': {
            currentListItemNumber[listItemAttrs.depth]++
            if (!['', '\n'].includes(node.textContent)) {
              let listItemNumbers = stripTrailingZeroes(currentListItemNumber)
              if (listItemAttrs.depth + 1 < listItemNumbers.length) {
                // if we're going up a level, reset the numbers for the next level and beyond
                currentListItemNumber = merge(
                  times(10, () => 0),
                  currentListItemNumber.slice(0, listItemAttrs.depth + 1),
                )
                listItemNumbers = stripTrailingZeroes(currentListItemNumber)
              }
              const listItemNumberAsString = listItemNumbers.join('.') + '.'
              markdown += `${'  '.repeat(listItemAttrs.depth)} ${listItemNumberAsString} ${node.textContent}\n`
            }

            break
          }
          default:
            if (!['', '\n'].includes(node.textContent)) {
              markdown += `${'  '.repeat(listItemAttrs.depth)} - ${node.textContent}\n`
            }
            break
        }
        shouldDescend = false
        break
      }
      case 'line_divider':
        markdown += '---\n'
        shouldDescend = false
        break

      // case 'blockquote':
      //   markdown += '> ' + node.textContent
      //   break
      // case 'bullet_list':
      //   markdown += '* ' + node.textContent
      //   break
      // case 'ordered_list':
      //   markdown += '1. ' + node.textContent
      //   break
      // case 'list_item':
      //   markdown += '- ' + node.textContent
      //   break
      // case 'horizontal_rule':
      //   markdown += '---'
      //   break
      // case 'image':
      //   markdown += '![' + node.attrs.alt + '](' + node.attrs.src + ')'
      //   break
      // case 'hard_break':
      //   markdown += '\n'
      //   break
      // case 'code_block':
      //   markdown += '```' + node.attrs.language + '\n' + node.textContent + '\n```'
      //   break
      // case 'text':
      //   markdown += node.textContent
      //   break
      // case 'em':
      //   markdown += '*' + node.textContent + '*'
      //   break
      // case 'strong':
      //   markdown += '**' + node.textContent + '**'
      //   break
      // case 'link':
      //   markdown += '[' + node.attrs.href + '](' + node.attrs.href + ')'
      //   break
      // case 'code':
      //   markdown += '`' + node.textContent + '`'
      //   break
      // case 'doc':
      //   break
      default:
        // eslint-disable-next-line no-console
        console.warn(`unknown node type: ${node.type.name}`, node.toJSON())
        break
    }
    return shouldDescend
  })
  return markdown
}

/**
 * Given a `source` string length, a position `pos` inside the string, and a `budget`, return a
 * tuple consisting of the first position and last position of the string to return, or only the first
 * position if `omitSuffix` is true, such that cutting the string at `sourceString.slice(start, pos)`
 * and `sourceString.slice(pos, end)` will return all the characters before and after the position,
 * and summing the lengths of the returned strings will be as close to the budget as possible.
 * @param source The length of the source string to operate on, assumed to be very long
 * @param pos A position/index inside the source string
 * @param budget How many characters to return in total
 * @param omitSuffix If true, only return the start boundary
 * @returns a number or a tuple of numbers in the form [start, end], where `start` is the index of
 * the first character in the string to return, and `end` is the index of the last character in the
 * string to return. The returned strings will be as long as possible, but will not exceed the budget.
 */
export function getCuttingBoundaries(
  source: number,
  pos: number,
  budget: number,
  omitSuffix?: false | undefined,
): [number, number]
export function getCuttingBoundaries(source: number, pos: number, budget: number, omitSuffix: true): number
export function getCuttingBoundaries(source: number, pos: number, budget: number, omitSuffix?: boolean) {
  if (source <= budget) {
    return omitSuffix ? 0 : [0, source]
  }

  let start: number = 0
  let end: number = 0
  const halfBudget = Math.floor(budget / 2)
  if (pos < halfBudget) {
    start = 0
    end = budget
  } else if (source - pos <= halfBudget) {
    start = source - budget
    end = source
  } else {
    start = pos - halfBudget
    end = pos + halfBudget
  }

  return omitSuffix ? start : [start, end]
}
